import { useLocalStorageWithSubscription } from "@sundae/react-hooks";
import { IMappedAsset, useWalletDetails } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";
import { useEffect, useMemo, useState } from "react";

import { LOCAL_STORAGE_PENDING_YF_TRANSACTIONS } from "../../constants/localStorage.constants";
import { Position } from "../../gql/generated/yieldFarmingV2.sdk";
import { useAssetMetadata } from "../../gql/hooks/assets.query";
import {
  useYieldFarmingPositionsQuery,
  useYieldFarmingProgramsQuery,
} from "../../gql/hooks/farms.query";
import { useRawPoolsByAssetIds } from "../../gql/hooks/pools.query";
import { IAssetMetaData } from "../../types/Asset.types";
import { IEnhancedMergedPosition } from "../../types/Farm.types";
import { createAssetMap } from "../../utils/assets.utils";
import {
  enhanceMergedPositionDelegations,
  enhanceMergedPositionValues,
  extractLpAssetsFromPositions,
  mergePositions,
} from "../../utils/farms.utils";
import { getPoolMapByLPAssetId } from "../../utils/pool.utils";
import { useAdaData } from "../useAdaData";
import { useAssetsWithBalance } from "../useAssetsWithBalance";

export interface IUseYieldFarmingPositionsArgs {
  beneficiary: string[];
}

export type TPendingYieldFarmingTransaction = {
  address: string;
  expires: number;
  txHash: string;
  type: "position" | "delegation" | "unlockingPosition";
};

export const useYieldFarmingPositions = ({ beneficiary }: IUseYieldFarmingPositionsArgs) => {
  const { details } = useWalletDetails();
  /* -------------------------------------------------------------------------------------------------
   * Flag to enable/disable refetch interval for positions
   * -----------------------------------------------------------------------------------------------*/
  const [enableRefetchInterval, setEnableRefetchInterval] = useState(false);

  /* -------------------------------------------------------------------------------------------------
   * Local Storage State for Pending Transactions
   * -----------------------------------------------------------------------------------------------*/
  const [pendingYieldFarmingTransactions, setPendingYieldFarmingTransactions] =
    useLocalStorageWithSubscription(
      LOCAL_STORAGE_PENDING_YF_TRANSACTIONS,
      [] as TPendingYieldFarmingTransaction[]
    );

  /* -------------------------------------------------------------------------------------------------
   * ADA Price
   * -----------------------------------------------------------------------------------------------*/
  const { data: adaData } = useAdaData();

  /* -------------------------------------------------------------------------------------------------
   * Query Yield Farming Data and User Balance
   * -----------------------------------------------------------------------------------------------*/
  const { data: programs } = useYieldFarmingProgramsQuery();
  const { data: positions } = useYieldFarmingPositionsQuery({
    beneficiary: beneficiary.filter((addr) => Boolean(addr)).sort(),
    enableRefetchInterval,
  });
  const {
    assetsWithBalance,
    balanceLoaded,
    lpAssetIds: lpAssetIdsFromBalance,
  } = useAssetsWithBalance();

  const lpAssetIds = useMemo(() => {
    const ids = lpAssetIdsFromBalance?.concat(
      extractLpAssetsFromPositions(positions as Position[])
    );
    return [...new Set(ids)];
  }, [lpAssetIdsFromBalance, positions]);

  /* -------------------------------------------------------------------------------------------------
   * Create a list of all available asset ids from
   * - Positions
   * - LP Assets
   * -----------------------------------------------------------------------------------------------*/
  const assetIds = useMemo(() => {
    const positionValueAssetIds = [
      ...new Set(positions?.flatMap(({ value }) => value)?.flatMap(({ assetID }) => assetID)),
    ];
    return [...positionValueAssetIds, ...lpAssetIds];
  }, [positions, lpAssetIds]);

  /* -------------------------------------------------------------------------------------------------
   * Create a list of all available staked asset ids based on the individual Yield Farming Programs
   * -----------------------------------------------------------------------------------------------*/
  const stakedAssetIds = useMemo(() => {
    return (
      positions
        ?.flatMap(({ delegation }) =>
          delegation
            ?.filter(({ program, pool }) => program && program?.stakedAsset && pool?.lpAsset)
            ?.flatMap(({ program, pool }) => [
              program?.stakedAsset as string,
              pool?.lpAsset as string,
            ])
        )
        .filter(Boolean) ?? []
    );
  }, [positions, lpAssetIds]);

  /* -------------------------------------------------------------------------------------------------
   * Fetch Pool and Asset Information based on
   * - Asset Ids (Pool information)
   * - Staked Asset Ids (Pool & Asset information)
   * -----------------------------------------------------------------------------------------------*/
  const { data: poolsForLps } = useRawPoolsByAssetIds({
    assetIds: lpAssetIds,
  });
  const { data: assets } = useAssetMetadata([...new Set([...stakedAssetIds, ...assetIds])]);

  /** If the user has all of his balance for a specific token locked, it will _not_ appear in his wallet, so creating
   * the assetMap with just the `assetsWithBalance` does not make too much sense.
   * We need to:
   * - Fetch all assets based on _all_ available asset ids we have (balance, position, lp assets)
   * - Return them as IMappedAsset<IAssetMetadata & IAsset>
   * - Merge them with the ones from the users' balance
   */
  const allAvailableAssets = useMemo(() => {
    if (!balanceLoaded || !assetsWithBalance || !assets) return;
    const assetsWithMetadata: IMappedAsset<IAssetMetaData>[] = assets.map((asset) => ({
      metadata: asset,
      quantity: new AssetAmount(0n),
      utxos: [],
    }));
    return uniqBy([...assetsWithBalance, ...assetsWithMetadata], "metadata.assetId");
  }, [assetsWithBalance, assets, balanceLoaded]);

  /* -------------------------------------------------------------------------------------------------
   * Create a map for all available LP Assets to easily access Pool and Asset Information.
   * -----------------------------------------------------------------------------------------------*/
  const lpAssetsPoolMap = useMemo(() => {
    if (!poolsForLps || !lpAssetIds) return;
    return getPoolMapByLPAssetId(poolsForLps, lpAssetIds);
  }, [poolsForLps, lpAssetIds]);

  /* -------------------------------------------------------------------------------------------------
   * Loading State
   * -----------------------------------------------------------------------------------------------*/
  const isLoading = useMemo(
    () => !balanceLoaded || !programs || !positions || !lpAssetsPoolMap,
    [balanceLoaded, programs, positions, lpAssetsPoolMap]
  );

  /* -------------------------------------------------------------------------------------------------
   * Enhance the Position Data with Pool and Asset Information.
   * -----------------------------------------------------------------------------------------------*/
  const mergedPosition: IEnhancedMergedPosition | undefined = useMemo(() => {
    if (isLoading) {
      return;
    }
    const merged = mergePositions(positions, programs, lpAssetsPoolMap);
    const assetMap = createAssetMap(allAvailableAssets || []);

    const positionValues = enhanceMergedPositionValues(
      merged?.value,
      assetMap,
      lpAssetsPoolMap,
      adaData?.usd
    );
    const positionDelegations = enhanceMergedPositionDelegations(
      merged?.delegation,
      lpAssetsPoolMap,
      assets
    );

    return {
      ...merged,
      delegation: positionDelegations ?? [],
      value: positionValues ?? [],
    };
  }, [allAvailableAssets, isLoading, lpAssetsPoolMap, positions, programs]);

  // Get all pending Transactions for `position` related transactions.
  const pendingPositionTransactions = useMemo(() => {
    return pendingYieldFarmingTransactions.filter(({ type, txHash: txHashFromPosition }) => {
      // If there are no inputs, we can assume that the user has no positions and therefore no pending transactions.
      if (isEmpty(mergedPosition?.inputs)) return true;
      return (
        type === "position" &&
        mergedPosition?.inputs?.some(({ txHash }) => txHash !== txHashFromPosition)
      );
    });
  }, [mergedPosition?.inputs, pendingYieldFarmingTransactions]);

  // Get all pending Transactions for `position` related transactions.
  const pendingUnlockingPositionTransactions = useMemo(() => {
    return pendingYieldFarmingTransactions.filter(({ type, txHash: txHashFromPosition }) => {
      return (
        type === "unlockingPosition" &&
        (mergedPosition?.inputs?.some(({ txHash }) => txHash !== txHashFromPosition) ||
          isEmpty(mergedPosition?.inputs))
      );
    });
  }, [mergedPosition?.inputs, pendingYieldFarmingTransactions]);

  // Get all pending Transactions for `delegated` related transactions.
  const pendingDelegationTransactions = useMemo(() => {
    return pendingYieldFarmingTransactions.filter(
      ({ type, txHash: txHashFromDelegation }) =>
        type === "delegation" &&
        mergedPosition?.inputs?.some(({ txHash }) => txHash !== txHashFromDelegation)
    );
  }, [mergedPosition?.inputs, pendingYieldFarmingTransactions]);

  /**
   * Effect to enable the refetch interval for the `useYieldFarmingPositionsQuery` hook.
   * This is only enabled if there are pending transactions for the user.
   */
  useEffect(() => {
    setEnableRefetchInterval(
      pendingPositionTransactions.length > 0 ||
        pendingDelegationTransactions.length > 0 ||
        pendingUnlockingPositionTransactions.length > 0
    );
  }, [
    pendingDelegationTransactions,
    pendingPositionTransactions,
    pendingUnlockingPositionTransactions,
  ]);

  /**
   * Effect to remove pending transactions for positions and delegations if they're older than 10 minutes.
   */
  useEffect(() => {
    const currentTime = Date.now();
    const filteredPendingTransactions = pendingYieldFarmingTransactions.filter(
      ({ address, expires }) => {
        // Only return items that are not older than 10 minutes and are for the current wallet.
        return currentTime < expires && details?.stakingAddress === address;
      }
    );

    if (filteredPendingTransactions.length !== pendingYieldFarmingTransactions.length) {
      setPendingYieldFarmingTransactions(filteredPendingTransactions);
    }
  }, []);

  /**
   * Effect to remove pending transactions for positions and delegations as soon as they are confirmed.
   */
  useEffect(() => {
    const successfulPositionTransactions = pendingYieldFarmingTransactions.filter(
      ({ type, txHash: txHashFromPosition }) =>
        type === "position" &&
        mergedPosition?.inputs?.some(({ txHash }) => txHash === txHashFromPosition)
    );

    const successfulDelegationTransactions = pendingYieldFarmingTransactions.filter(
      ({ type, txHash: txHashFromDelegation }) =>
        type === "delegation" &&
        mergedPosition?.inputs?.some(({ txHash }) => txHash === txHashFromDelegation)
    );

    if (successfulPositionTransactions.length) {
      setPendingYieldFarmingTransactions((prev) =>
        prev.filter(
          ({ txHash: txHashFromPosition }) =>
            !successfulPositionTransactions.some(({ txHash }) => txHashFromPosition === txHash)
        )
      );
    }

    if (successfulDelegationTransactions.length) {
      setPendingYieldFarmingTransactions((prev) =>
        prev.filter(
          ({ txHash: txHashFromPosition }) =>
            !successfulDelegationTransactions.some(({ txHash }) => txHashFromPosition === txHash)
        )
      );
    }

    if (
      pendingUnlockingPositionTransactions.length &&
      mergedPosition &&
      Object.values(mergedPosition).every((value) => isEmpty(value))
    ) {
      // clear all pending position and unlockingPosition transactions if the user has no positions anymore.
      setPendingYieldFarmingTransactions((prev) => {
        return prev.filter(({ type }) => type !== "position" && type !== "unlockingPosition");
      });
    }
  }, [
    pendingUnlockingPositionTransactions,
    pendingYieldFarmingTransactions,
    mergedPosition,
    setPendingYieldFarmingTransactions,
  ]);

  return {
    //existingPositionsUtxos,
    assets: allAvailableAssets,
    isLoading,
    lpAssetsPoolMap,
    mergedPosition,
    pendingPositionTransactions,
    pendingDelegationTransactions,
    pendingUnlockingPositionTransactions,
    pendingYieldFarmingTransactions,
    setPendingYieldFarmingTransactions,
  };
};
